import { PDFDocument } from 'pdf-lib'
import { zipSync, type Zippable } from 'fflate'

/**
 * Extracts the given (1-indexed) pages from `file` and returns a new PDF blob.
 * Used for the instant-download path so users never have to give us their
 * email — splitting happens in their browser.
 */
export async function extractPagesAsPdf(file: File, pages: number[]): Promise<Blob> {
  if (!Array.isArray(pages) || pages.length === 0) {
    throw new Error('No pages selected for this invoice.')
  }

  const srcBytes = await file.arrayBuffer()

  let src: PDFDocument
  try {
    src = await PDFDocument.load(srcBytes, { ignoreEncryption: true })
  } catch (e) {
    throw new Error(`Could not read source PDF: ${(e as Error).message ?? e}`)
  }

  const totalPages = src.getPageCount()
  const indices: number[] = []
  for (const p of pages) {
    if (!Number.isInteger(p) || p < 1 || p > totalPages) {
      throw new Error(
        `Page ${p} is out of range (PDF has ${totalPages} page${totalPages === 1 ? '' : 's'}).`,
      )
    }
    indices.push(p - 1)
  }

  const out = await PDFDocument.create()
  const copied = await out.copyPages(src, indices)
  copied.forEach((page) => out.addPage(page))

  const bytes = await out.save()
  const buffer = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(buffer).set(bytes)
  return new Blob([buffer], { type: 'application/pdf' })
}

/**
 * Bundles already-extracted PDF blobs into a single zip blob using fflate.
 * Synchronous (zipSync) since the bundles are tiny relative to the PDFs
 * themselves — async wouldn't measurably help here.
 */
export async function zipFiles(
  files: { filename: string; blob: Blob }[],
): Promise<Blob> {
  const entries: Zippable = {}
  for (const f of files) {
    const buf = new Uint8Array(await f.blob.arrayBuffer())
    // De-dupe filename collisions by appending a counter — rare but possible
    // if two invoices ended up with the same suggested filename.
    let name = f.filename
    let i = 1
    while (entries[name]) {
      const ext = name.toLowerCase().endsWith('.pdf') ? '.pdf' : ''
      const base = ext ? name.slice(0, -4) : name
      name = `${base}-${i}${ext}`
      i++
    }
    // Disable per-entry compression — PDFs are already compressed, so the
    // STORE method is faster and produces a near-identical-sized zip.
    entries[name] = [buf, { level: 0 }]
  }
  const zipped = zipSync(entries)
  // Same defensive copy we use elsewhere to satisfy TS's strict Blob types.
  const buffer = new ArrayBuffer(zipped.byteLength)
  new Uint8Array(buffer).set(zipped)
  return new Blob([buffer], { type: 'application/zip' })
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Brief delay so the browser starts the download before the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
